import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3.5 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

export default function AdminForgot() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email }, { withCredentials: true });
      setSent(data.message || "If an account exists for this email, a reset link has been sent.");
      toast.success("Reset link sent (अगर account मौजूद है)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 pt-28" data-testid="admin-forgot-view">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        className="card-glow w-full max-w-md rounded-2xl p-8 sm:p-10"
        data-testid="admin-forgot-form"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
          <KeyRound size={20} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold uppercase text-white">Forgot password</h1>
        <p className="mt-2 text-sm text-neutral-400">
          अपना owner email डालें — reset link उसी inbox में जाएगा (1 घंटे valid)।
        </p>

        {sent ? (
          <div className="mt-7 rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/5 p-5" data-testid="forgot-success">
            <p className="flex items-start gap-3 text-sm leading-relaxed text-[#e6b800]">
              <Mail size={16} className="mt-0.5 shrink-0" />
              {sent}
            </p>
            <p className="mt-3 text-xs text-neutral-500">
              Email न आए तो spam folder check करें — या 2 मिनट बाद दोबारा try करें।
            </p>
          </div>
        ) : (
          <div className="mt-7 space-y-4">
            <div>
              <label htmlFor="forgot-email" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                Email
              </label>
              <input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="owner@ishumotors.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-button"
              className="w-full rounded-full bg-[#e6b800] py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-50"
            >
              {loading ? "Sending..." : "Send reset link"}
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between text-xs">
          <Link to="/admin" data-testid="forgot-back-link" className="text-neutral-400 transition-colors hover:text-[#e6b800]">
            ← Login पर वापस
          </Link>
          <Link to="/admin/signup" data-testid="forgot-signup-link" className="text-[#e6b800] transition-colors hover:text-[#ffc700]">
            नया account बनाएँ
          </Link>
        </div>
      </motion.form>
    </div>
  );
}
